import { signal, WritableSignal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormGroup } from "@angular/forms";

import {
  FieldErrorConfig,
  FieldErrorState,
  FieldErrorStateMap,
  FormValidationOptions,
  FormValidationResult,
} from "./form-validation.types";

/**
 * Creates error state signals for a single field
 */
function createFieldErrorState(): FieldErrorState {
  return {
    errorMessage: signal<string>(""),
    showError: signal<boolean>(false),
  };
}

/**
 * Determines if an error should be shown for a control
 */
function shouldShowError(control: AbstractControl): boolean {
  return control.invalid && (control.dirty || control.touched);
}

/**
 * Gets the error message for a control based on its current errors
 */
function getErrorMessageForControl(
  control: AbstractControl,
  errorMessages: Record<
    string,
    string | ((control: AbstractControl) => string)
  >,
): string {
  for (const [errorKey, message] of Object.entries(errorMessages)) {
    if (control.hasError(errorKey)) {
      return typeof message === "function" ? message(control) : message;
    }
  }
  return "";
}

/**
 * Creates a function that updates a specific field's error state
 */
function createFieldErrorUpdater(
  form: FormGroup,
  controlName: string,
  config: FieldErrorConfig,
  state: FieldErrorState,
): () => void {
  return () => {
    const control = form.get(controlName);
    if (!control) return;

    const show = shouldShowError(control);
    state.showError.set(show);

    const message = getErrorMessageForControl(control, config.errorMessages);
    state.errorMessage.set(message);
  };
}

/**
 * Sets up validation listeners for all configured fields.
 * Must be called from a constructor or injection context for takeUntilDestroyed to work.
 *
 * @param options - The form and field configurations
 * @returns FormValidationResult with all validation utilities
 */
export function setupFormValidation(
  options: FormValidationOptions,
): FormValidationResult {
  const { form, fieldConfigs } = options;
  const errorStates: FieldErrorStateMap = new Map();
  const fieldUpdaters: Map<string, () => void> = new Map();

  // Create error states and updaters for each field
  for (const config of fieldConfigs) {
    const state = createFieldErrorState();
    errorStates.set(config.controlName, state);

    const updater = createFieldErrorUpdater(
      form,
      config.controlName,
      config,
      state,
    );
    fieldUpdaters.set(config.controlName, updater);

    // Set up value change listener
    const control = form.get(config.controlName);
    if (control) {
      control.valueChanges
        .pipe(takeUntilDestroyed())
        .subscribe(() => updater());
    }
  }

  const updateFieldError = (controlName: string): void => {
    const updater = fieldUpdaters.get(controlName);
    if (updater) {
      updater();
    }
  };

  const updateAllErrors = (): void => {
    for (const updater of fieldUpdaters.values()) {
      updater();
    }
  };

  const validate = (): boolean => {
    if (form.invalid) {
      form.markAllAsTouched();
      updateAllErrors();
      return false;
    }
    return true;
  };

  const markAllAsTouched = (): void => {
    form.markAllAsTouched();
    updateAllErrors();
  };

  const resetErrors = (): void => {
    for (const state of errorStates.values()) {
      state.showError.set(false);
      state.errorMessage.set("");
    }
  };

  const getErrorMessage = (
    controlName: string,
  ): WritableSignal<string> | undefined => {
    return errorStates.get(controlName)?.errorMessage;
  };

  const getShowError = (
    controlName: string,
  ): WritableSignal<boolean> | undefined => {
    return errorStates.get(controlName)?.showError;
  };

  return {
    errorStates,
    updateFieldError,
    updateAllErrors,
    validate,
    markAllAsTouched,
    resetErrors,
    getErrorMessage,
    getShowError,
  };
}
